<template>
  <nav
    v-if="breadcrumbs.length"
    class="breadcrumbs relative flex flex-nowrap items-center text-EXPONDO-secondary"
    aria-label="breadcrumb"
  >
    <button
      v-if="showLeftArrow"
      class="flex items-center"
      :aria-label="$t('Scroll left')"
      @click="onClickLeftArrow"
    >
      <span class="iconify svg--chevron-left text-2xl" />
    </button>
    <ol
      ref="scrollContainer"
      class="breadcrumbs__list flex overflow-scroll select-none
        [scrollbar-width:none] [&::-webkit-scrollbar]:hidden list-none"
      itemscope
      :itemtype="SCHEMA_ITEM_TYPE.BREADCRUMB_LIST"
    >
      <li>
        <span
          ref="firstItemHtml"
          class="block w-4"
        />
      </li>
      <li
        class="breadcrumbs__list-item"
      >
        <SimpleLink
          to="/"
          class="breadcrumbs__breadcrumb hover:no-underline"
          :aria-label="$t('expondo Home Page')"
        >
          <span class="iconify svg--home" />
        </SimpleLink>
      </li>
      <li
        v-for="(breadcrumb, i) in breadcrumbs"
        :key="breadcrumb.slug"
        class="breadcrumbs__list-item relative"
        :itemprop="SCHEMA_ITEM_PROP.ITEM_LIST_ELEMENT"
        itemscope
        :itemtype="SCHEMA_ITEM_TYPE.BREADCRUMB_LIST_ITEM"
      >
        <SimpleLink
          :to="breadcrumbLink(breadcrumb.link)"
          class="breadcrumbs__breadcrumb h-full flex"
          :class="{'font-bold': lastItem(i, breadcrumbs.length)}"
          :itemprop="SCHEMA_ITEM_PROP.ITEM"
          @link-click="handleClick({
            categoryName: breadcrumb.text,
            categoryLevel: i
          })"
        >
          <span
            class="px-2"
          >/</span>
          <span
            class="whitespace-nowrap underline"
            :itemprop="SCHEMA_ITEM_PROP.NAME"
          >{{ breadcrumb.text }}</span>
        </SimpleLink>
        <meta
          :itemprop="SCHEMA_ITEM_PROP.POSITION"
          :content="i"
        >
      </li>
      <li>
        <span
          ref="lastItemHtml"
          class="block w-4"
        />
      </li>
    </ol>
    <button
      v-if="showRightArrow"
      class="flex items-center"
      :aria-label="$t('Scroll right')"
      @click="onClickRightArrow"
    >
      <span class="iconify svg--chevron-right text-2xl" />
    </button>
  </nav>
</template>

<script lang="ts">
import { useIntersectionObserver } from '@vueuse/core';
import type { Breadcrumb } from '~~/types/components/Breadcrumbs/Breadcrumb';
import { getPageLinkForSlug } from '~~/helpers/routes/getPageInformation';

import { SCHEMA_ITEM_PROP, SCHEMA_ITEM_TYPE, BREADCRUMBS_HOMEPAGE_PROP_NAME } from '~~/constants/seo';
import { SCROLL_BEHAVIOR_SMOOTH } from '~~/constants/ui';
import { buildCategoryPageUrl } from '~~/helpers/category/buildCategoryPageUrl';

export default defineComponent({
  name: 'Breadcrumbs',
  props: {
    breadcrumbs: {
      type: Array as PropType<Array<Breadcrumb>>,
      default: () => [],
    },
    isCategoryLink: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {

    const BREADCRUMB_SCROLL_OFFSET = 200;
    const { trackClickBreadcrumbs } = useGtmEvents();
    const { getCurrentPageType } = usePageEventLocation();
    const { countryCode } = useLocalization();
    const showRightArrow = ref(false);
    const showLeftArrow = ref(false);
    const lastItemHtml = ref<HTMLElement | null>(null);
    const firstItemHtml = ref<HTMLElement | null>(null);
    const scrollContainer = ref<HTMLElement | null>(null);
    const lastItem = (index, breadcrumbsLength) => breadcrumbsLength - 1 === index;
    const breadcrumbLink = (breadcrumbLink) => {

      if (props.isCategoryLink) {

        return buildCategoryPageUrl({
          category: breadcrumbLink,
          countryCode: countryCode.value,
        });
      
      }

      return getPageLinkForSlug(breadcrumbLink);
    
    };

    const currentPageType = getCurrentPageType();

    const handleClick = ({
      categoryName,
      categoryLevel,
    }: {
      categoryName: string;
      categoryLevel: number;
    }) => {

      trackClickBreadcrumbs({
        categoryName,
        categoryPath: window.location.href,
        categoryLevel,
        source: currentPageType,
      });
    
    };

    const handleIntersectionCallback = (entries: IntersectionObserverEntry[]): void => {

      entries.forEach((entry: IntersectionObserverEntry) => {

        if (entry.isIntersecting) {

          showRightArrow.value = false;
        
        } else {

          showRightArrow.value = true;
        
        }
      
      });
    
    };
    const handleIntersectionCallbackFirstItem = (entries: IntersectionObserverEntry[]): void => {

      entries.forEach((entry: IntersectionObserverEntry) => {

        if (entry.isIntersecting) {

          showLeftArrow.value = false;
        
        } else {

          showLeftArrow.value = true;
        
        }
      
      });
    
    };
    useIntersectionObserver(lastItemHtml, handleIntersectionCallback);
    useIntersectionObserver(firstItemHtml, handleIntersectionCallbackFirstItem);

    const onClickRightArrow = () => {

      if (scrollContainer.value) {

        scrollContainer.value.scrollBy({ left: BREADCRUMB_SCROLL_OFFSET, behavior: SCROLL_BEHAVIOR_SMOOTH });
      
      }
    
    };
    const onClickLeftArrow = () => {

      if (scrollContainer.value) {

        scrollContainer.value.scrollBy({ left: -BREADCRUMB_SCROLL_OFFSET, behavior: SCROLL_BEHAVIOR_SMOOTH });
      
      }
    
    };

    return {
      lastItem,
      breadcrumbLink,
      SCHEMA_ITEM_TYPE,
      SCHEMA_ITEM_PROP,
      BREADCRUMBS_HOMEPAGE_PROP_NAME,
      handleClick,
      lastItemHtml,
      showRightArrow,
      onClickRightArrow,
      scrollContainer,
      onClickLeftArrow,
      showLeftArrow,
      firstItemHtml,
    };
  
  },
});
</script>
